/* Обзор — сводный экран группы. ТЗ 6.1, 7.3. */

import { canEdit, canRead, crmLevel, ddmm, dmy, heroGradient, plural, rub, sectionLevel, toneColor } from "@mera/shared";
import React, { useRef, useState } from "react";
import { useNavigate } from "../lib/router.jsx";

import { Ion } from "../components/icons.jsx";
import { Avatar } from "../components/AvatarPicker.jsx";
import GoalLadder from "../components/GoalLadder.jsx";
import Screen from "../components/Screen.jsx";
import { SkelOverview, SkelRows } from "../components/skeleton.jsx";
import {
  BurnCards, Empty, ErrorState, Eyebrow, Hint, Sep,
} from "../components/ui.jsx";
import { useApp } from "../lib/store.jsx";
import { useFetch } from "../lib/store.jsx";
import { haptic } from "../lib/tg.js";


export default function Overview() {
  const { me } = useApp();
  const { data, loading, error, refetch } = useFetch("/dashboard");
  const { data: news, loading: newsLoading } = useFetch("/news");
  const [openForecast, setOpenForecast] = useState(false);
  /* Высоту раскрытого прогноза меряем по самому списку: платежей бывает
     сколько угодно, и на глаз выставленный потолок обрезал бы хвост */
  const forecastBody = useRef(null);
  const [forecastHeight, setForecastHeight] = useState(0);
  /* Слайд главной карточки — он же фильтр по бизнесам: первый срез общий,
     дальше по одному на бизнес. Всё ниже читает выбранный срез. */
  const [slide, setSlide] = useState(0);
  const navigate = useNavigate();

  /* Список перерисовывается при смене бизнеса и после загрузки данных,
     поэтому меряем на каждом проходе — одинаковая высота состояние не меняет */
  React.useLayoutEffect(() => {
    const el = forecastBody.current;
    if (el) setForecastHeight(el.scrollHeight);
  });

  // Права: чтение открывает экран, редактирование — быстрые действия с деньгами
  if (!canRead(sectionLevel(me, "overview"))) {
    return <Screen logo><ErrorState text="Раздел закрыт администратором." /></Screen>;
  }
  const ovEdit = canEdit(sectionLevel(me, "overview"));

  if (loading) return <Screen logo><SkelOverview /></Screen>;
  if (error) return <Screen logo><ErrorState text={error} onRetry={refetch} /></Screen>;

  /* Срезов всегда хотя бы один — общий по группе. Слайд мог остаться от прошлых
     данных: бизнес успели заархивировать, пока экран был открыт */
  const views = data.views || [];
  const active = Math.min(slide, views.length - 1);
  const view = views[active];
  if (!view) return <Screen logo><ErrorState text="Обзор не собрался" onRetry={refetch} /></Screen>;

  const { totals, forecastBreakdown, funnels } = view;

  return (
    <Screen logo>
      {/* 1. Баланс и запас хода одной карточкой (ТЗ 6.1.3) */}
      <BalanceSlider
        views={views}
        index={active}
        onIndex={setSlide}
        onAdd={ovEdit ? () => { haptic.medium(); navigate("/edit/operations/new"); } : null}
      />
      <BurnCards
        total={totals.balance}
        burn={totals.burn}
        onBurnClick={me?.isAdmin ? () => { haptic.light(); navigate("/manage/recurring"); } : null}
      />

      {/* 2. Твёрдый прогноз поступлений (ТЗ 6.1.1) */}
      <div className="px-4 pt-2">
        <div
          className="card-tg px-4 py-3.5 press"
          onClick={() => {
            haptic.light();
            setOpenForecast((v) => !v);
          }}
          role="button"
        >
          <div className="eyebrow px-0 pt-0 pb-1.5">Прогноз поступлений</div>
          <div className="flex items-start justify-between gap-3">
            <div className="num text-[26px] font-bold leading-none">{rub(totals.totalRev)}</div>
            <Ion.chevronDown
              size={20}
              className="mt-0.5"
              style={{ color: "var(--tg-hint)", transform: openForecast ? "rotate(180deg)" : "none", transition: "transform .2s" }}
            />
          </div>
          <div className="mt-1.5 text-[13px]" style={{ color: "var(--tg-hint)" }}>
            {totals.totalCount} {plural(totals.totalCount, "платёж", "платежа", "платежей")}
          </div>

          {/* Раскрытие анимируем высотой: при простом монтировании закрытие было бы рывком.
              Высоту меряем по содержимому — фиксированный потолок обрезал длинный список */}
          <div
            className="overflow-hidden transition-[max-height,opacity] duration-200 ease-out"
            style={{ maxHeight: openForecast ? forecastHeight : 0, opacity: openForecast ? 1 : 0 }}
          >
            {/* Отступ внутрь, а не полем: внешнее поле не попадает в scrollHeight */}
            <div ref={forecastBody} className="pt-3">
              <BreakdownList
                items={forecastBreakdown.pending}
                onOpen={ovEdit ? (pid) => { haptic.light(); navigate(`/edit/payments/${pid}`); } : null}
              />
              <BreakdownList title="Следующий MRR" items={forecastBreakdown.mrr} />
            </div>
          </div>
        </div>
      </div>

      {/* 3. Лестница целей (ТЗ 5.9, 6.1.2) — она же заменила шкалу кэшфлоу: цель
          по доходу теперь ступень лестницы, и две шкалы про одно и то же не нужны.
          Цели общие для группы, поэтому срез по бизнесу их не меняет: деньги
          считаются по всем счетам, доход — по всему журналу */}
      <GoalLadder
        goals={data.goals}
        onOpen={me?.isAdmin ? () => { haptic.light(); navigate("/manage/goals"); } : null}
      />

      {/* 5. Воронки CRM по бизнесам (ТЗ 6.1.4).
          С фильтром остаётся воронка одного бизнеса, а у продукта её может не быть вовсе.
          Закрытые правами воронки не показываем — их и в CRM не открыть */}
      {funnels.filter((f) => canRead(crmLevel(me, f.funnel, "clients"))).length ? (
        <>
          <Eyebrow>Воронки продаж</Eyebrow>
          <div className="px-4 grid grid-cols-2 gap-2">
            {funnels.filter((f) => canRead(crmLevel(me, f.funnel, "clients"))).map((f) => (
              <div
                key={f.funnel}
                className="card-tg px-3.5 py-3 active:opacity-70"
                role="button"
                onClick={() => {
                  haptic.light();
                  navigate(`/crm?funnel=${f.funnel}`);
                }}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  {/* Кружок — цвет бизнеса из настроек. Не выбран — кружка нет:
                      подставлять вместо него оценку воронки мы перестали */}
                  {toneColor(f.tone) ? (
                    <span
                      className="inline-block rounded-full shrink-0 w-2 h-2"
                      style={{ background: toneColor(f.tone) }}
                    />
                  ) : null}
                  {/* Название приходит с воронкой: бизнесы заводятся в справочнике,
                      и списком в коде их не перечислить. Рядом — число сделок */}
                  <span className="eyebrow px-0 pt-0 pb-0 truncate">{f.name || f.funnel}</span>
                  <span className="eyebrow num px-0 pt-0 pb-0 shrink-0">({f.deals})</span>
                </div>
                <div className="num text-[18px] font-bold leading-tight">{rub(f.revenue)}</div>
                {/* MRR показываем всегда: ноль — тоже ответ, а пропажа строки читалась
                    бы как «данных нет». В скобках — сколько сделок его платят */}
                <div className="mt-0.5 text-[12px]" style={{ color: "var(--tg-hint)" }}>
                  MRR <span className="num">{rub(f.mrr || 0)}</span> ({f.mrrCount || 0})
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {/* 6. Лента новостей (ТЗ 6.1.7) */}
      <Eyebrow>Лента</Eyebrow>
      {/* Лента приезжает отдельным запросом — до ответа держим её место строками,
          иначе «Новостей пока нет» мигает там, где новости есть */}
      {newsLoading ? (
        <SkelRows rows={3} right={false} />
      ) : (
      <div className="px-4">
        <div className="card-tg py-1">
          {!news?.length ? (
            <Empty text="Новостей пока нет" icon={Ion.newspaper} />
          ) : (
            news.slice(0, 8).map((n, i) => (
              <React.Fragment key={n.id}>
                {i > 0 ? <Sep /> : null}
                <div className="px-4 py-2.5">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[13px] font-semibold">{n.who || "Система"}</span>
                    <Hint>{ddmm(n.at)}</Hint>
                  </div>
                  <div className="text-[14px] leading-snug">{n.t}</div>
                </div>
              </React.Fragment>
            ))
          )}
        </div>
      </div>
      )}
    </Screen>
  );
}

/* Строка прогноза кликабельна, если за ней стоит плановый платёж: ведём в его
   карточку — там его правят, удаляют и отмечают оплаченным. */
/* Заголовок необязателен: у ожидаемых платежей его нет — они идут первыми
   и так понятно, что раскрыт прогноз */
function BreakdownList({ title, items, onOpen }) {
  if (!items?.length) return null;
  return (
    <div className="mt-2">
      {title ? <div className="eyebrow px-0 pt-1 pb-1">{title}</div> : null}
      {items.map((p, i) => {
        const clickable = !!(p.id && onOpen);
        /* Под названием — только дата платежа. Поле «когда ждём» рядом с ней есть,
           но это свободный текст («до 20 июля», «Q3»), и датой он не притворяется:
           dmy резал его по позициям символов и выдавал мусор вроде «юл.20 .до 2». */
        return (
          <div
            key={p.id ?? i}
            /* Название с датой слева, сумма справа: сумма и шеврон держатся вместе,
               иначе распорка растаскивала их по строке в разные места */
            className={`flex items-center gap-3 py-1.5 ${clickable ? "press" : ""}`}
            role={clickable ? "button" : undefined}
            onClick={clickable ? (e) => { e.stopPropagation(); onOpen(p.id); } : undefined}
          >
            <div className="min-w-0 flex-1">
              <div className="text-[14px] truncate leading-tight">{p.client}</div>
              {p.dueDate ? <Hint className="block leading-tight">{dmy(p.dueDate)}</Hint> : null}
            </div>
            <span className="num text-[14px] font-semibold shrink-0">{rub(p.amount)}</span>
            {clickable ? (
              <Ion.chevronForward size={13} style={{ color: "var(--tg-hint)" }} className="shrink-0" />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/* Слайдер главной карточки: первый слайд — деньги всей группы, дальше по бизнесу.
   Он же фильтр «Обзора», поэтому активный слайд поднят в состояние экрана.
   Прокрутка настоящая, пальцем: слайд прилипает (card-slider), а индекс мы просто
   читаем из scrollLeft — так жест остаётся нативным, без перехвата touch-событий. */
function BalanceSlider({ views, index, onIndex, onAdd }) {
  const track = useRef(null);
  /* Пока едет плавная прокрутка от нажатия на точку, чужие scroll-события не слушаем:
     иначе по дороге с первого слайда на третий экран моргнул бы вторым. */
  const gliding = useRef(false);
  const timer = useRef(null);

  React.useEffect(() => () => clearTimeout(timer.current), []);

  /* Шаг берём по соседним слайдам, а не по ширине окна: в шаг входит зазор,
     и при одном слайде делить было бы не на что */
  const step = () => {
    const kids = track.current?.children;
    if (!kids?.length) return 0;
    return kids.length > 1 ? kids[1].offsetLeft - kids[0].offsetLeft : kids[0].offsetWidth;
  };

  const onScroll = (e) => {
    const s = step();
    if (!s || gliding.current) return;
    const next = Math.round(e.currentTarget.scrollLeft / s);
    if (next !== index && next >= 0 && next < views.length) {
      haptic.select();
      onIndex(next);
    }
  };

  const goTo = (i) => {
    if (i === index) return;
    haptic.select();
    onIndex(i);
    gliding.current = true;
    track.current?.parentElement?.scrollTo({ left: i * step(), behavior: "smooth" });
    // О конце плавной прокрутки браузер не сообщает — снимаем блокировку по таймеру
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { gliding.current = false; }, 400);
  };

  /* Нажатие на бизнес в общей карточке — то же, что свайп до его слайда */
  const goToBiz = (id) => {
    const i = views.findIndex((v) => v.id === id);
    if (i > 0) goTo(i);
  };

  return (
    <>
      <div className="card-slider pt-2" onScroll={onScroll}>
        <div ref={track} className="flex gap-2 ps-4">
          {views.map((v) => (
            <div key={v.id} className="snap-start shrink-0 w-[calc(100vw-2rem)]">
              <BalanceCard
                view={v}
                onAdd={onAdd}
                onPickBiz={views.length > 1 ? goToBiz : null}
              />
            </div>
          ))}
          {/* Правое поле — отдельной распоркой: ни padding контейнера, ни margin
              последнего слайда в ширину прокрутки не попадают, и карточка упиралась
              в край экрана. Ширина — поле за вычетом зазора между слайдами */}
          <div aria-hidden className="shrink-0 w-2" />
        </div>
      </div>
      <Dots count={views.length} index={index} onPick={goTo} />
    </>
  );
}

/* Точки под карточкой: и указатель места в слайдере, и кнопки перехода.
   Активная — вытянутая, как в ленте фотографий iOS. */
function Dots({ count, index, onPick }) {
  if (count < 2) return null;
  return (
    <div className="flex items-center justify-center gap-1.5 pt-2.5">
      {Array.from({ length: count }, (_, i) => (
        <button
          key={i}
          type="button"
          aria-label={`Слайд ${i + 1} из ${count}`}
          aria-current={i === index}
          onClick={() => onPick(i)}
          className="h-1.5 rounded-full transition-all duration-200 active:opacity-60"
          style={{
            width: i === index ? 18 : 6,
            background: i === index
              ? "var(--tg-button)"
              : "color-mix(in srgb, var(--tg-hint) 40%, transparent)",
          }}
        />
      ))}
    </div>
  );
}

/* Баланс одной карточкой: сумма крупно, под ней строки. У группы строка — бизнес,
   у бизнеса — его счёт. Контура как отдельной сущности нет: деньги лежат на счетах
   бизнеса, и делить их на нал с безналом больше незачем.
   Цвет карточки — цвет бизнеса из настроек; у общей карточки и без выбранного
   цвета остаётся базовая тёмно-синяя заливка из card-hero. */
function BalanceCard({ view, onAdd, onPickBiz }) {
  const tint = toneColor(view.tone);

  return (
    <div
      className="card-hero overflow-hidden relative h-full"
      style={tint ? { background: heroGradient(tint) } : undefined}
    >
      {/* Запись операции — самое частое действие, поэтому кнопка прямо на балансе.
          Без права правок кнопки нет: баланс остаётся только чтением */}
      {onAdd ? (
        <button
          type="button"
          onClick={onAdd}
          aria-label="Добавить операцию"
          /* top-4 — та же линия, что у аватарки бизнеса слева (верхнее поле карточки) */
          className="absolute right-3 top-4 w-12 h-12 rounded-full flex items-center justify-center active:opacity-60"
          style={{ background: "rgba(255,255,255,0.16)", color: "#fff" }}
        >
          <Ion.add size={28} />
        </button>
      ) : null}

      {/* Аватарка бизнеса слева от суммы: в его карточке строки — счета, и без неё
          слайд не отличить от общего. У группы аватарки нет — там она в строках */}
      {/* items-start, а не по центру: аватарка встаёт ровно на верхнее поле карточки —
          на ту же линию, что кнопка «+» справа. По центру текста она уезжала на пару пикселей */}
      <div className="px-4 pt-4 pb-3 flex items-start gap-3">
        {/* 48 px — размер кнопки «+» справа, они держат одну строку карточки */}
        {view.id === "all" ? null : <Avatar src={view.avatar} name={view.name} size={48} onDark />}
        <div className="min-w-0 flex-1">
          <div className="eyebrow px-0 pt-0 pb-1 pe-14 truncate">
            {view.id === "all" ? "Всего денег" : view.name}
          </div>
          <div className="num text-[32px] font-bold leading-none">{rub(view.totals.balance)}</div>
        </div>
      </div>

      {!view.rows.length ? (
        <div className="px-4 pb-4 text-[13px] text-white/55">Счетов пока нет</div>
      ) : null}

      {/* В общей карточке строка — бизнес: жмём и слайдер едет на его карточку.
          В карточке бизнеса строка — счёт, там ни аватарки, ни значка не нужно */}
      {view.rows.map((r) => {
        const isBiz = !!r.accounts;
        const pick = isBiz && onPickBiz ? () => { haptic.light(); onPickBiz(r.id); } : null;
        return (
          <div
            key={r.id}
            /* Последняя строка не должна упираться в нижний край карточки */
            className={`flex items-center gap-3 px-4 py-1.5 last:pb-3.5 ${pick ? "active:opacity-60" : ""}`}
            role={pick ? "button" : undefined}
            onClick={pick || undefined}
          >
            {isBiz ? <Avatar src={r.avatar} name={r.name} size={38} onDark /> : null}
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-medium truncate">{r.name}</div>
            </div>
            <span className="num text-[15px] font-semibold shrink-0">{rub(r.amount)}</span>
          </div>
        );
      })}
    </div>
  );
}

